import { inject, Injectable } from '@angular/core'

import { difference, sortBy, uniqWith } from 'lodash-es'
import { takeUntil, timer } from 'rxjs'

import { Vehicle } from '@ti/contracts'
import { ONE_MINUTE_MS } from '@ti/core/common/constants'
import { VehicleTelemetry } from '@ti/core/contracts/vehicle'
import { BaseTelemetryProvider } from '@ti/core/flespi-providers'
import { VehicleStatus } from '@ti/shared/map/contracts'

import { FlespiClient } from './flespi.client'

@Injectable({ providedIn: 'root' })
export class TelemetryProvider extends BaseTelemetryProvider<VehicleTelemetry> {
  protected readonly flespiClient = inject(FlespiClient)

  protected readonly INACTIVE_THRESHOLD = 15 * ONE_MINUTE_MS
  protected readonly MAX_RECENT_POSITIONS = 25

  public async initialize(vehicles: Vehicle[]) {
    await Promise.all([
      this.loadDataEngineStatuses(vehicles).catch((error) => console.error(error)),
      this.loadInitialTelemetry(vehicles).catch((error) => console.error(error)),
      this.subscribeToTelemetry(vehicles).catch((error) => console.error(error)),
    ])

    this.loadRecentPositions(vehicles).catch((error) =>
      console.error(`Cannot load telemetry recent positions`, error),
    )

    timer(0, 2000)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.updateLastStatusValidity())

    this.resetFlespiTokenOnSignOut()
  }

  public async loadDataEngineSecondaryData(vehicle: Vehicle) {
    const telemetry = await this.flespiClient.loadDataEngineSecondaryData(vehicle)
    this.updateTelemetryMap(telemetry)
  }

  public setUpdatedOdometerValue(deviceId: number, odometer: number) {
    this.updateTelemetryMap({ deviceId, odometer })
  }

  public setUpdatedEngineHoursValue(deviceId: number, engineHours: number) {
    this.updateTelemetryMap({ deviceId, engineHours })
  }

  protected async loadInitialTelemetry(vehicles: Vehicle[]) {
    const telemetry = await this.flespiClient.loadInitialTelemetry(vehicles)

    // Assign 'N/A' statuses
    telemetry.forEach((telemetry) => {
      telemetry.battery = telemetry.battery ?? null
      telemetry.ignitionStatus = telemetry.ignitionStatus ?? null
    })

    this.updateTelemetryMap(telemetry)
  }

  protected async loadDataEngineStatuses(vehicles: Vehicle[]) {
    const statuses = await this.flespiClient.loadDataEngineStatuses(vehicles)

    // Check all statuses to appear properly
    const hasInvalidStatus = statuses.some((s) => !s.deviceId)
    if (hasInvalidStatus) {
      const validStatuses = statuses.filter((s) => s.deviceId)
      const vehicleDeviceIds = vehicles.map((v) => v.primaryExternalDeviceId)
      const invalidDeviceIds = difference(
        vehicleDeviceIds,
        validStatuses.map((s) => s.deviceId),
      )
      this.updateTelemetryMap(
        validStatuses.concat(
          invalidDeviceIds.map((deviceId) => {
            return {
              deviceId,
              lastStatus: null,
            } as VehicleTelemetry
          }),
        ),
      )
    } else {
      this.updateTelemetryMap(statuses)
    }
  }

  protected async subscribeToTelemetry(vehicles: Vehicle[]) {
    this.startTelemetrySubscription((token, onAuthFailure) =>
      this.flespiClient.createFlespiSubscription(
        vehicles,
        token,
        (data) => this.updateTelemetryMap(data),
        onAuthFailure,
      ),
    )
  }

  // Should be called after initial telemetry and statuses are loaded
  protected async loadRecentPositions(vehicles: Vehicle[]) {
    const targetVehicles = vehicles.filter((v) => {
      const telemetry = this.telemetryMap$.value.get(v.primaryExternalDeviceId)
      return telemetry?.lastStatus === VehicleStatus.Running
    })

    const positionsMap = await this.flespiClient.massLoadVehicleRecentPositions(targetVehicles)
    const telemetryUpdates = new Array<Partial<VehicleTelemetry>>()
    positionsMap.forEach((positions, vehicleId) => {
      const relatedVehicle = vehicles.find((v) => v.id === vehicleId)
      if (relatedVehicle) {
        telemetryUpdates.push({
          deviceId: relatedVehicle.primaryExternalDeviceId,
          recentPositions: positions,
        })
      }
    })
    this.updateTelemetryMap(telemetryUpdates)
  }

  // Derives lastStatus/lastStatusChangeAt from speed & ignition and merges recent positions
  // and custom sensors - fleet-only enrichment tsp telemetry has no analog for.
  protected override enrichTelemetryUpdate(
    updates: Partial<VehicleTelemetry>,
    current: VehicleTelemetry | undefined,
  ): void {
    const RECENT_POSITIONS_THRESHOLD = Date.now() - 2 * ONE_MINUTE_MS

    const lastSpeed = updates.speed ?? current?.speed
    const lastSpeedTimestamp = updates.timestamps?.position ?? current?.timestamps?.position
    const lastIgnition = updates.ignitionStatus ?? current?.ignitionStatus
    const lastIgnitionTimestamp =
      updates.timestamps?.ignitionStatus ?? current?.timestamps?.ignitionStatus

    if (lastSpeed) {
      updates.lastStatus = VehicleStatus.Running
      if (current?.lastStatus !== updates.lastStatus) {
        updates.lastStatusChangeAt = lastSpeedTimestamp
      }
      updates.timestamps = updates.timestamps || {}
      updates.timestamps.lastStatus = lastSpeedTimestamp
    } else {
      if (lastIgnition) {
        updates.lastStatus = VehicleStatus.Idling
        // for last true ignition value that was before moving stopped - it should be start of stopping
        if (current?.lastStatus !== updates.lastStatus) {
          updates.lastStatusChangeAt =
            updates?.lastStatusChangeAt ||
            Math.max(lastSpeedTimestamp ?? 0, lastIgnitionTimestamp ?? 0)
        }
        updates.timestamps = updates.timestamps || {}
        updates.timestamps.lastStatus = Math.max(
          lastIgnitionTimestamp ?? 0,
          updates.lastStatusChangeAt ?? 0,
        )
      } else {
        updates.lastStatus = VehicleStatus.Stopped
        if (current?.lastStatus !== updates.lastStatus) {
          updates.lastStatusChangeAt =
            updates?.lastStatusChangeAt ||
            lastSpeedTimestamp ||
            updates.timestamps?.lastStatus ||
            updates.lastReportedAt
        }
        updates.timestamps = updates.timestamps || {}
        updates.timestamps.lastStatus = lastSpeedTimestamp
      }
    }

    if (current) {
      // Accumulate recent positions
      const relevantLastStatus = updates.lastStatus ?? current.lastStatus
      if (relevantLastStatus === VehicleStatus.Running && updates.latitude && updates.longitude) {
        updates.recentPositions = (updates.recentPositions || []).concat({
          latitude: updates.latitude,
          longitude: updates.longitude,
          timestamp: updates.timestamps.position ?? Date.now(),
        })
      }

      // Reset recent positions when the vehicle is not running
      if (relevantLastStatus !== VehicleStatus.Running && current.recentPositions?.length) {
        updates.recentPositions = []
      }

      // Merge last positions when loaded additional
      if (updates.recentPositions?.length) {
        const relevantPositions = (
          current.recentPositions?.length
            ? [...current.recentPositions, ...updates.recentPositions]
            : updates.recentPositions
        ).filter((position) => position.timestamp >= RECENT_POSITIONS_THRESHOLD)

        // Concat a previous position with newly loaded data, then sort DESC
        const sortedUniquePositions = relevantPositions.length
          ? sortBy(
              uniqWith(
                relevantPositions,
                (a, b) => a.latitude + a.longitude === b.latitude + b.longitude,
              ),
              ['timestamp'],
            )
          : []

        // Keep the limited number of the most recent positions
        updates.recentPositions =
          sortedUniquePositions.length > this.MAX_RECENT_POSITIONS
            ? sortedUniquePositions.slice(
                sortedUniquePositions.length - this.MAX_RECENT_POSITIONS,
                sortedUniquePositions.length,
              )
            : sortedUniquePositions
      }

      // Merge custom sensors
      updates.customSensors = {
        ...(current.customSensors || {}),
        ...(updates.customSensors || {}),
      }
    }
  }

  protected updateLastStatusValidity() {
    let hasUpdates = false
    const telemetryMap = new Map(this.telemetryMap$.getValue())
    const statusInactiveAt = Date.now() - this.INACTIVE_THRESHOLD // 15 min
    telemetryMap.forEach((telemetry, deviceId) => {
      const isInvalid = this.isLastStatusInvalid(telemetry, statusInactiveAt)
      if (isInvalid !== telemetry.lastStatusInvalid) {
        telemetryMap.set(deviceId, { ...telemetry, lastStatusInvalid: isInvalid })
        hasUpdates = true
      }
    })
    if (hasUpdates) {
      this.telemetryMap$.next(telemetryMap)
    }
  }

  protected isLastStatusInvalid(telemetry: VehicleTelemetry, statusInactiveAt: number): boolean {
    if (telemetry.lastStatus === undefined || !telemetry?.timestamps?.lastStatus) {
      return true
    }
    if (telemetry.lastStatus === VehicleStatus.Idling) {
      return telemetry?.timestamps?.lastStatus <= statusInactiveAt
    }
    return telemetry.lastStatus === VehicleStatus.Running
      ? telemetry?.timestamps?.lastStatus <= statusInactiveAt
      : false
  }
}
